import { MATCH_CAPACITY, MIXED_MAX_PER_GENDER, type MatchDoc } from './match'
import { vouchingExhausted, type VouchingRecord } from './seat-identity'

// Pure join/leave logic — the single source for seat math + eligibility.
// The repository applies these patches inside a Firestore TRANSACTION (two
// concurrent joins serialize; the loser re-reads and sees the seat gone) and
// the security rules validate the same shape server-side, so a hand-rolled
// client can't oversubscribe or dodge eligibility either.

export type JoinBlock =
  | 'already-joined'
  | 'full'
  | 'not-active'
  | 'needs-gender'
  | 'wrong-gender'
  | 'mixed-side-full'
  | 'not-a-player'
  | 'not-a-placeholder'
  | 'vouch-limit'

export interface JoinerInfo {
  uid: string
  name: string
  photoURL: string | null
  /** From the joiner's own users doc; null = undeclared. */
  gender: 'male' | 'female' | null
  /** Joined by name from a shared link (anonymous session, no profile). */
  isGuest?: boolean
}

/** Null when the join is allowed, else the reason it must be blocked. */
export function joinBlockReason(doc: MatchDoc, joiner: JoinerInfo): JoinBlock | null {
  if (doc.players.includes(joiner.uid)) return 'already-joined'
  // No privacy branch here any more. "Privado" means the match does not appear
  // in the public feed — it does NOT mean invite-only (decision 2026-08-08).
  //
  // This used to return 'private-not-invited' for anyone without an invite doc,
  // which locked out every friend the organizer reached by WhatsApp: their uid
  // is minted the moment they tap join, so an invite doc for it cannot exist.
  // Meanwhile the app kept offering Compartir/WhatsApp on that same match.
  // Discovery is what privacy protects, and `allow list` still protects it.
  if (doc.status !== 'active') return 'not-active'
  if (doc.availableSlots <= 0) return 'full'
  const cat = doc.genderCategory
  if (cat === 'women' || cat === 'men' || cat === 'mixed') {
    if (joiner.gender == null) return 'needs-gender'
    if (cat === 'women' && joiner.gender !== 'female') return 'wrong-gender'
    if (cat === 'men' && joiner.gender !== 'male') return 'wrong-gender'
    // MIXTO IS 2+2. The comment here used to read "'mixed' joins like open
    // until the pairing CF lands" — true while the rules also let it through.
    // The rules started enforcing a per-gender cap on 2026-08-11, and leaving
    // this guard behind meant the client waved a join through that Firestore
    // then refused: a tap, a spinner, and a generic error with no gender
    // prompt. A client guard that disagrees with the rules is worse than none,
    // because it turns a clear refusal into a mystery.
    if (cat === 'mixed' && seatsOfGender(doc, joiner.gender) >= MIXED_MAX_PER_GENDER) {
      return 'mixed-side-full'
    }
  }
  return null
}

/**
 * How many seats already hold `g`. Mirrors `countGender()` in firestore.rules —
 * same source (`playersInfo`), same treatment of a missing gender (counts as
 * neither). If these two ever disagree, the client either blocks a join the
 * server would allow or waves through one it refuses; both are worse than the
 * check not existing.
 */
function seatsOfGender(doc: MatchDoc, g: 'male' | 'female'): number {
  return Object.values(doc.playersInfo ?? {}).filter((p) => p?.gender === g).length
}

/** The exact field patch a join writes (rules validate this same shape). */
export function buildJoinPatch(doc: MatchDoc, joiner: JoinerInfo): Record<string, unknown> {
  const slots = doc.availableSlots - 1
  return {
    players: [...doc.players, joiner.uid],
    [`playersInfo.${joiner.uid}`]: {
      name: joiner.name,
      photoURL: joiner.photoURL,
      // THE SEAT MUST DECLARE ITS GENDER, or a mixto join is refused by the
      // rules with a bare 403.
      //
      // `ownSeatGenderIs()` compares this field to the joiner's PROFILE gender,
      // so omitting it made every mixto join impossible from the app — the rule
      // shipped 2026-08-11 and the client was never taught to satisfy it.
      //
      // Nine rules tests covered mixto and all nine passed, because each one
      // hand-built its join patch WITH a gender. They tested a write shape this
      // function never produces. A fixture that does not match the production
      // writer proves the rule, not the app.
      ...(joiner.gender ? { gender: joiner.gender } : {}),
      ...(joiner.isGuest ? { guest: true } : {}),
    },
    availableSlots: slots,
    status: slots === 0 ? 'full' : 'active',
  }
}

export type LeaveBlock = 'not-joined' | 'organizer-cannot-leave'

export function leaveBlockReason(doc: MatchDoc, uid: string): LeaveBlock | null {
  if (!doc.players.includes(uid)) return 'not-joined'
  // The organizer cancels (affects everyone) — never silently abandons.
  if (doc.organizer === uid) return 'organizer-cannot-leave'
  return null
}

export function buildLeavePatch(doc: MatchDoc, uid: string): Record<string, unknown> {
  const slots = Math.min(doc.availableSlots + 1, MATCH_CAPACITY)
  return {
    players: doc.players.filter((p) => p !== uid),
    availableSlots: slots,
    // A full match reopens; cancelled/completed stay terminal (leave is
    // blocked for those upstream anyway — status here is active|full only).
    status: doc.status === 'full' ? 'active' : doc.status,
  }
}

// ── PLACEHOLDER SEATS (spec/02 § Seats & identity) ───────────────────────────
// A seat for somebody who is not on the app. It holds a uid like every other
// seat, so the anonymous companion counter disappears and all roster logic
// stays uniform. Every patch below must match `firestore.rules` EXACTLY —
// isAddPlaceholder / isRemovePlaceholder / isClaimPlaceholder — because a shape
// the rules reject is a silent permission-denied at the worst moment.

/**
 * `ph_`-prefixed, and that prefix is STRUCTURAL, not cosmetic. A Firebase uid is
 * 28 alphanumerics and never contains `_`, so this can never collide with a real
 * identity. Without it, a client could insert a real person's uid into
 * `players` labelled as a placeholder — and `rosterValidResult()` gates on
 * `players`, so a recorded result would rewrite the level of someone who never
 * joined. That is B1's exact shape; the rules enforce the prefix.
 */
export function newPlaceholderUid(): string {
  const rand = Math.random().toString(36).slice(2, 10)
  return `ph_${Date.now().toString(36)}${rand}`
}

export function buildAddPlaceholderPatch(
  doc: MatchDoc,
  args: { uid: string; name: string; createdBy: string; gender?: 'male' | 'female' },
): Record<string, unknown> {
  const slots = doc.availableSlots - 1
  return {
    // Appended LAST: the rules read the added uid from `removeAll`, and the
    // seat math below is what stops two seats being consumed for one decrement.
    players: [...doc.players, args.uid],
    [`playersInfo.${args.uid}`]: {
      name: args.name,
      photoURL: null,
      identity: 'placeholder',
      // Never anyone else: this is what a no-show is charged against, so
      // writing another uid here would mint debt in their name. Rules enforce.
      createdBy: args.createdBy,
      // Mixto counts seats, and a companion is half the roster of one. A seat
      // with no gender cannot be counted, so the label would promise a 2+2
      // nothing enforces. Omitted entirely for non-mixto matches rather than
      // written null — an absent gender is "not applicable", not "unknown".
      ...(args.gender ? { gender: args.gender } : {}),
    },
    availableSlots: slots,
    status: slots === 0 ? 'full' : 'active',
  }
}

export function buildRemovePlaceholderPatch(doc: MatchDoc, uid: string): Record<string, unknown> {
  const slots = doc.availableSlots + 1
  return {
    players: doc.players.filter((p) => p !== uid),
    [`playersInfo.${uid}`]: null,
    availableSlots: slots,
    status: 'active',
  }
}

/**
 * Claiming — the ONLY patch that removes and adds a uid in one write.
 *
 * `availableSlots` is deliberately absent: the seat was already paid for when
 * the placeholder was created, and the rules refuse the write if it moves. That
 * single omission is what stops a claim manufacturing a free seat.
 */
export function buildClaimPatch(
  doc: MatchDoc,
  args: { placeholderUid: string; claimer: JoinerInfo },
): Record<string, unknown> {
  const info = doc.playersInfo[args.placeholderUid]
  return {
    players: [...doc.players.filter((p) => p !== args.placeholderUid), args.claimer.uid],
    [`playersInfo.${args.placeholderUid}`]: null,
    [`playersInfo.${args.claimer.uid}`]: {
      // Keep the name the seat was created with unless the claimer gave one —
      // they are the same person, and the roster should not flicker.
      name: args.claimer.name || info?.name || 'Jugador',
      photoURL: args.claimer.photoURL,
      identity: args.claimer.isGuest ? 'guest' : 'account',
    },
  }
}

/**
 * Null when the placeholder may be added, else why not.
 *
 * `record` is the actor's vouching slice of `players_public`. Passing it is
 * REQUIRED rather than optional: an overload that defaulted to "no limit" would
 * make every caller that forgot it silently uncapped, which is precisely how
 * the cap shipped as a spec clause with no code the first time.
 */
export function addPlaceholderBlockReason(
  doc: MatchDoc,
  actor: string,
  record: VouchingRecord | null,
): JoinBlock | null {
  if (doc.status !== 'active') return 'not-active'
  if (doc.availableSlots <= 0) return 'full'
  if (!doc.players.includes(actor)) return 'not-a-player'
  // Checked LAST: "the match is full" is a fact about the match and "you have
  // no room left" is a fact about you. Reporting the personal one when the
  // impersonal one also applies reads as an accusation for no reason.
  if (vouchingExhausted(record)) return 'vouch-limit'
  return null
}

/** Null when the seat may be claimed by `claimer`, else why not. */
export function claimBlockReason(
  doc: MatchDoc,
  placeholderUid: string,
  claimer: string,
): JoinBlock | null {
  if (!['active', 'full'].includes(doc.status)) return 'not-active'
  if (doc.players.includes(claimer)) return 'already-joined'
  const info = doc.playersInfo[placeholderUid]
  if (!info || info.identity !== 'placeholder') return 'not-a-placeholder'
  return null
}
